<template>
  <div class="ebook-list">
    <a-card :bordered="false" title="电子书管理">
      <a-form layout="inline" class="toolbar-form">
        <a-form-item label="关键词">
          <a-input
            v-model="listQuery.keyword"
            allow-clear
            placeholder="书名、作者、ISBN"
            style="width: 200px"
            @pressEnter="fetchList(1)"
          />
        </a-form-item>
        <a-form-item label="分类">
          <a-select
            v-model="listQuery.categoryId"
            allow-clear
            placeholder="全部"
            style="width: 200px"
            show-search
            option-filter-prop="children"
          >
            <a-select-option v-for="opt in filterCategoryOptions" :key="opt.value" :value="opt.value">
              {{ opt.label }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="标签">
          <a-select
            v-model="listQuery.tagId"
            allow-clear
            placeholder="全部"
            style="width: 180px"
            show-search
            option-filter-prop="children"
          >
            <a-select-option v-for="opt in filterTagOptions" :key="opt.value" :value="opt.value">
              {{ opt.label }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item>
          <a-button type="primary" @click="fetchList(1)">查询</a-button>
        </a-form-item>
        <a-form-item>
          <CommonPermissions :p="$permissions.TEXTBOOK.CREATE">
            <a-button type="primary" icon="plus" @click="openCreateModal">新增电子书</a-button>
          </CommonPermissions>
        </a-form-item>
      </a-form>

      <a-table
        row-key="id"
        :columns="columns"
        :data-source="ebookList"
        :loading="loading"
        :pagination="{ current: currentPage, total: total, pageSize: pageSize, showSizeChanger: false }"
        @change="handleTableChange"
      >
        <template slot="thumb" slot-scope="_, record">
          <img
            class="thumb-cell thumb-clickable"
            :src="record.thumbUrl || defaultThumb"
            alt=""
            title="点击查看大图"
            @click="openThumbPreview(record)"
          />
        </template>
        <template slot="categories" slot-scope="_, record">
          <span class="categories-cell" :title="formatCategoryNames(record)">{{ formatCategoryNames(record) }}</span>
        </template>
        <template slot="tags" slot-scope="_, record">
          <div class="tags-cell" :title="formatTagNamesPlain(record)">
            <template v-if="getRecordTagItems(record).length">
              <a-tag v-for="t in getRecordTagItems(record)" :key="'tag-' + t.id" class="ebook-tag-pill">{{ t.name }}</a-tag>
            </template>
            <span v-else>—</span>
          </div>
        </template>
        <template slot="titleAuthorPrice" slot-scope="_, record">
          <div class="ebook-meta-col">
            <div class="ebook-meta-line">
              <span class="ebook-meta-k">书名</span>
              <span class="ebook-meta-v ellipsis-plain" :title="record.title != null ? String(record.title) : ''">{{
                cellDisplayText(record.title)
              }}</span>
            </div>
            <div class="ebook-meta-line">
              <span class="ebook-meta-k">作者</span>
              <span class="ebook-meta-v ellipsis-plain" :title="cellDisplayText(record.authors)">{{
                cellDisplayText(record.authors)
              }}</span>
            </div>
            <div class="ebook-meta-line">
              <span class="ebook-meta-k">售价</span>
              <span class="ebook-meta-v">{{ formatPriceYuan(record.price) }}</span>
            </div>
            <div class="ebook-meta-line">
              <span class="ebook-meta-k">格式</span>
              <span class="ebook-meta-v">{{ formatBookFormatLabel(record.bookFormat) }}</span>
            </div>
          </div>
        </template>
        <template slot="publisherDate" slot-scope="_, record">
          <div class="ebook-meta-col">
            <div class="ebook-meta-line">
              <span class="ebook-meta-k">出版单位</span>
              <span class="ebook-meta-v ellipsis-plain" :title="cellDisplayText(record.publishers)">{{
                cellDisplayText(record.publishers)
              }}</span>
            </div>
            <div class="ebook-meta-line">
              <span class="ebook-meta-k">出版日期</span>
              <span class="ebook-meta-v">{{ formatIssuedDate(record.issuedDate) }}</span>
            </div>
          </div>
        </template>
        <template slot="forSale" slot-scope="text">
          <a-tag v-if="text === true" color="green">开售</a-tag>
          <a-tag v-else-if="text === false" color="red">未开售</a-tag>
          <span v-else>—</span>
        </template>
        <template slot="status" slot-scope="text">
          <a-tag v-if="text === 0">未上架</a-tag>
          <a-tag v-else-if="text === 1" color="green">已上架</a-tag>
          <a-tag v-else-if="text === 2" color="red">已删除</a-tag>
          <span v-else>—</span>
        </template>
        <template slot="action" slot-scope="_, row">
          <CommonPermissions :p="$permissions.TEXTBOOK.UPDATE">
            <a @click="openInfoDrawer(row.id)">本书信息</a>
          </CommonPermissions>
          <CommonPermissions :p="$permissions.TEXTBOOK.UPDATE">
            <a-divider type="vertical" />
            <a @click="openCategoryTagModal(row)">上架分类</a>
          </CommonPermissions>
          <CommonPermissions :p="$permissions.TEXTBOOK.UPDATE">
            <a-divider type="vertical" />
            <a @click="openPdfModal(row)">文件</a>
          </CommonPermissions>
          <CommonPermissions :p="$permissions.TEXTBOOK.DELETE">
            <a-divider type="vertical" />
            <a-popconfirm title="确定删除该电子书？" @confirm="handleDelete(row.id)">
              <a class="danger-link">删除</a>
            </a-popconfirm>
          </CommonPermissions>
        </template>
      </a-table>
    </a-card>

    <a-modal
      :visible="thumbPreviewVisible"
      :footer="null"
      centered
      width="720px"
      :title="thumbPreviewTitle"
      @cancel="closeThumbPreview"
    >
      <div class="thumb-preview-wrap">
        <img class="thumb-preview-img" :src="thumbPreviewUrl" alt="" />
      </div>
    </a-modal>

    <a-modal
      :title="pdfModalTitleText"
      :visible="pdfModalVisible"
      :footer="null"
      width="960px"
      destroy-on-close
      @cancel="closePdfModal"
    >
      <div class="pdf-modal-toolbar">
        <a-upload
          name="file"
          :show-upload-list="false"
          accept="application/pdf,.pdf"
          :custom-request="uploadEbookPdf"
          :before-upload="beforePdfUpload"
          @change="handlePdfUploadChange"
        >
          <a-button :loading="pdfUploading">
            <a-icon :type="pdfUploading ? 'loading' : 'upload'" />
            上传 PDF
          </a-button>
        </a-upload>
        <a-button
          v-if="ebookPdfPreviewUrl"
          type="default"
          style="margin-left: 8px"
          @click="openPdfPreviewInNewTab"
        >
          新标签打开
        </a-button>
      </div>
      <a-spin :spinning="pdfPreviewLoading" tip="加载预览中…">
        <template v-if="pdfModalHasPdf">
          <div v-if="ebookPdfPreviewUrl" class="pdf-preview-frame-wrap">
            <iframe :key="ebookPdfPreviewUrl" :src="ebookPdfPreviewUrl" class="pdf-preview-iframe" title="PDF 预览" />
          </div>
          <a-empty v-else-if="!pdfPreviewLoading" description="暂无预览地址" />
        </template>
        <a-empty v-else description="尚未上传 PDF，请先上传" />
      </a-spin>
    </a-modal>

    <a-drawer
      :title="infoDrawerTitle"
      placement="right"
      width="80%"
      :visible="infoDrawerVisible"
      @close="closeInfoDrawer"
    >
      <a-spin :spinning="infoLoading">
        <a-form-model :label-col="labelCol" :wrapper-col="wrapperCol">
          <a-form-model-item label="书名">
            <a-input v-model="form.title" />
          </a-form-model-item>
          <a-form-model-item label="作者">
            <a-input v-model="form.authors" />
          </a-form-model-item>
          <a-form-model-item label="主编">
            <a-input v-model="form.chiefEditor" />
          </a-form-model-item>
          <a-form-model-item label="副主编">
            <a-input v-model="form.deputyEditor" />
          </a-form-model-item>
          <a-form-model-item label="参编人员">
            <a-input v-model="form.contributingAuthors" />
          </a-form-model-item>
          <a-form-model-item label="ISBN">
            <a-input v-model="form.isbn" />
          </a-form-model-item>
          <a-form-model-item label="电子书格式">
            <a-select v-model="form.bookFormat" placeholder="请选择" style="width: 100%">
              <a-select-option v-for="item in bookFormatOptions" :key="item.value" :value="item.value">
                {{ item.label }}
              </a-select-option>
            </a-select>
          </a-form-model-item>
          <a-form-model-item label="售价(元)">
            <a-input-number v-model="form.price" :min="0" :precision="2" style="width: 100%" />
          </a-form-model-item>
          <a-form-model-item label="出版单位">
            <a-input v-model="form.publishers" />
          </a-form-model-item>
          <a-form-model-item label="出版社">
            <a-select v-model="form.publisherCompanyId" allow-clear style="width: 100%" placeholder="请选择">
              <a-select-option v-for="p in publisherCompanyList" :key="p.id" :value="p.id">{{ p.name }}</a-select-option>
            </a-select>
          </a-form-model-item>
          <a-form-model-item label="出版日期">
            <a-date-picker
              v-model="form.issuedDate"
              style="width: 100%"
              format="YYYY-MM-DD"
              placeholder="请选择出版日期"
              allow-clear
            />
          </a-form-model-item>
          <a-form-model-item label="制作单位">
            <a-input v-model="form.producer" />
          </a-form-model-item>
          <a-form-model-item label="版次">
            <a-input v-model="form.edition" />
          </a-form-model-item>
          <a-form-model-item label="联系信息">
            <a-input v-model="form.contactInfo" />
          </a-form-model-item>
          <a-form-model-item label="简介">
            <a-input v-model="form.summary" type="textarea" :auto-size="{ maxRows: 10 }" />
          </a-form-model-item>
          <a-form-model-item label="版权声明">
            <a-input v-model="form.copyrightStatement" type="textarea" :auto-size="{ maxRows: 10 }" />
          </a-form-model-item>
          <a-form-model-item label="课程详情模板">
            <div class="ebook-content-editor-wrap">
              <editor
                v-if="infoDrawerVisible && !infoLoading"
                :key="'ebook-content-' + (form.id || 'new')"
                v-model="form.content"
                :init="contentEditorInit"
              />
            </div>
          </a-form-model-item>
          <a-form-model-item label="封面">
            <Upload path="ebook/cover/" :default-url="form.thumbUrl" @changeImgUri="onThumbUri" />
          </a-form-model-item>
          <a-form-model-item label="卡片背景">
            <Upload path="ebook/card_bg/" :default-url="form.cardBgUrl" @changeImgUri="onCardBgUri" />
          </a-form-model-item>
          <a-form-model-item label="备注">
            <a-input v-model="form.note" type="textarea" :auto-size="{ minRows: 3 }" />
          </a-form-model-item>
          <a-form-model-item :wrapper-col="{ span: 14, offset: 4 }">
            <a-button type="primary" :loading="infoSaving" @click="saveInfo">保存</a-button>
            <a-button style="margin-left: 8px" @click="closeInfoDrawer">取消</a-button>
          </a-form-model-item>
        </a-form-model>
      </a-spin>
    </a-drawer>

    <a-modal
      title="电子书分类与标签"
      :visible="categoryTagModalVisible"
      :confirm-loading="categoryTagSubmitting"
      width="560px"
      destroy-on-close
      @ok="saveCategoryAndTag"
      @cancel="closeCategoryTagModal"
    >
      <a-form-model :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">
        <a-form-model-item label="电子书分类">
          <a-select
            v-model="categoryTagForm.categoryIds"
            mode="multiple"
            :max-tag-count="8"
            allow-clear
            placeholder="请选择（可多选，同属电子书分类）"
            @change="onCategoryIdsChange"
          >
            <a-select-option v-for="item in categoryModalOptions" :key="item.value" :value="item.value">
              {{ item.label }}
            </a-select-option>
          </a-select>
        </a-form-model-item>
        <a-form-model-item label="标签">
          <a-select
            v-model="categoryTagForm.tagIds"
            mode="multiple"
            :max-tag-count="8"
            allow-clear
            placeholder="请选择标签"
          >
            <a-select-option v-for="item in tagModalOptions" :key="item.value" :value="item.value">
              {{ item.label }}
            </a-select-option>
          </a-select>
        </a-form-model-item>
        <a-form-model-item label="状态">
          <a-select v-model="categoryTagForm.status" allow-clear placeholder="请选择">
            <a-select-option :value="0">未上架</a-select-option>
            <a-select-option :value="1">已上架</a-select-option>
            <a-select-option :value="2">已删除</a-select-option>
          </a-select>
        </a-form-model-item>
        <a-form-model-item label="排序">
          <a-input-number v-model="categoryTagForm.sortKey" :min="0" style="width: 100%" placeholder="数值越小越靠前" />
        </a-form-model-item>
        <a-form-model-item label="开放销售">
          <a-switch v-model="categoryTagForm.forSale" />
        </a-form-model-item>
      </a-form-model>
    </a-modal>

    <a-modal
      title="新增电子书"
      :visible="createModalVisible"
      :confirm-loading="createSubmitting"
      destroy-on-close
      @ok="submitCreate"
      @cancel="closeCreateModal"
    >
      <a-form-model :label-col="{ span: 5 }" :wrapper-col="{ span: 17 }">
        <a-form-model-item label="书名" required>
          <a-input v-model="createForm.title" placeholder="请输入书名" :max-length="200" />
        </a-form-model-item>
      </a-form-model>
    </a-modal>
  </div>
</template>

<script>
import Upload from '@/components/Upload.vue'
import CommonPermissions from '@/components/CommonPermissions'
import TextBookApi from '@/api/src/textbook/api/TextBookApi'
import DictApi from '@/api/src/basic/api/DictApi'
import { buildCategoryDisplayOptions } from '@/utils/textbookCategoryOptions'
import Editor from '@tinymce/tinymce-vue'
import 'tinymce/tinymce'
import 'tinymce/themes/silver/theme'
import 'tinymce/icons/default/icons'
import 'tinymce/plugins/link'
import 'tinymce/plugins/lists'
import 'tinymce/plugins/table'
import 'tinymce/plugins/code'
import 'tinymce/plugins/image'
import 'tinymce/plugins/media'
import 'tinymce/plugins/preview'

const textBookApi = new TextBookApi()
const dictApi = new DictApi()
const EBOOK_BOOK_TYPE = 2
const defaultThumb = 'https://fm-cloud-general-uestcp.oss-cn-chengdu.aliyuncs.com/common/no_thumb.png'
const OSS = require('ali-oss')

const bookFormatOptions = [
  { value: 0, label: 'App' },
  { value: 100, label: '自建H5' },
  { value: 101, label: '第三方H5' },
  { value: 200, label: 'PDF' },
]

function formatBookFormatLabel(value) {
  const item = bookFormatOptions.find((option) => option.value === Number(value))
  return item ? item.label : '—'
}

function emptyInfoForm() {
  return {
    id: undefined,
    title: '',
    authors: '',
    chiefEditor: '',
    deputyEditor: '',
    contributingAuthors: '',
    isbn: '',
    price: undefined,
    publishers: '',
    publisherCompanyId: undefined,
    issuedDate: null,
    producer: '',
    edition: '',
    contactInfo: '',
    summary: '',
    copyrightStatement: '',
    content: '',
    thumbUri: undefined,
    thumbUrl: '',
    cardBgUri: undefined,
    cardBgUrl: '',
    note: '',
    bookFormat: 0,
  }
}

export default {
  name: 'EbookList',
  components: { Upload, CommonPermissions, Editor },
  data() {
    return {
      contentEditorInit: {
        skin_url: '/tinymce/skins/ui/oxide',
        language_url: '/tinymce/langs/zh_CN.js',
        language: 'zh_CN',
        menubar: 'file edit view insert format tools table',
        plugins: ['link', 'lists', 'table', 'code', 'image', 'media', 'preview'],
        toolbar: [
          'undo redo | formatselect | bold italic underline strikethrough',
          'alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image media table | preview code',
        ],
        height: 480,
        branding: false,
        paste_data_images: true,
        content_style:
          'body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; font-size: 14px; line-height: 1.6; }',
      },
      defaultThumb,
      bookFormatOptions,
      thumbPreviewVisible: false,
      thumbPreviewUrl: '',
      thumbPreviewTitle: '',
      loading: false,
      ebookList: [],
      currentPage: 1,
      total: 0,
      pageSize: 12,
      listQuery: {
        keyword: undefined,
        categoryId: undefined,
        tagId: undefined,
      },
      filterCategoryOptions: [],
      filterTagOptions: [],

      columns: [
        { title: '封面', width: 60, key: 'thumb', scopedSlots: { customRender: 'thumb' } },
        {
          title: '书名 / 作者 / 售价',
          width: 280,
          key: 'titleAuthorPrice',
          scopedSlots: { customRender: 'titleAuthorPrice' },
        },
        {
          title: '出版单位 / 出版日期',
          width: 240,
          key: 'publisherDate',
          scopedSlots: { customRender: 'publisherDate' },
        },
        { title: '分类', key: 'categories', ellipsis: true, scopedSlots: { customRender: 'categories' } },
        { title: '标签', key: 'tags', scopedSlots: { customRender: 'tags' } },
        { title: '销售', dataIndex: 'forSale', scopedSlots: { customRender: 'forSale' } },
        { title: '状态', dataIndex: 'status', scopedSlots: { customRender: 'status' } },
        { title: '操作', key: 'action', scopedSlots: { customRender: 'action' } },
      ],

      publisherCompanyList: [],
      infoDrawerVisible: false,
      infoDrawerTitle: '电子书信息',
      infoLoading: false,
      infoSaving: false,
      form: emptyInfoForm(),
      labelCol: { span: 4 },
      wrapperCol: { span: 14 },

      categoryTagModalVisible: false,
      categoryTagSubmitting: false,
      currentEbookId: null,
      categoryModalOptions: [],
      tagModalOptions: [],
      categoryTagForm: {
        categoryIds: [],
        tagIds: [],
        status: undefined,
        sortKey: null,
        forSale: true,
      },

      createModalVisible: false,
      createSubmitting: false,
      createForm: { title: '' },

      pdfModalVisible: false,
      pdfModalEbookId: null,
      pdfModalTitle: '',
      pdfModalPdfFileSize: undefined,
      ebookPdfPreviewUrl: '',
      pdfPreviewLoading: false,
      pdfUploading: false,
    }
  },
  computed: {
    pdfModalHasPdf() {
      const n = this.pdfModalPdfFileSize
      return n != null && Number(n) > 0
    },
    pdfModalTitleText() {
      return this.pdfModalTitle ? `电子书 PDF — ${this.pdfModalTitle}` : '电子书 PDF'
    },
  },
  mounted() {
    this.initFilters()
    this.fetchList(1)
  },
  methods: {
    openThumbPreview(record) {
      const url = record && (record.thumbUrl || this.defaultThumb)
      this.thumbPreviewUrl = url || this.defaultThumb
      this.thumbPreviewTitle = (record && record.title) ? String(record.title) : '封面预览'
      this.thumbPreviewVisible = true
    },
    closeThumbPreview() {
      this.thumbPreviewVisible = false
      this.thumbPreviewUrl = ''
      this.thumbPreviewTitle = ''
    },
    formatCategoryNames(record) {
      const list = record && record.categories
      if (!Array.isArray(list) || !list.length) return '—'
      const names = list.map((c) => (c && c.name) || '').filter(Boolean)
      return names.length ? names.join('、') : '—'
    },
    cellDisplayText(val) {
      if (val == null) return '—'
      const s = String(val).trim()
      return s || '—'
    },
    formatPriceYuan(val) {
      if (val == null || val === '') return '—'
      const fen = Number(val)
      if (Number.isNaN(fen)) return '—'
      return (fen / 100).toFixed(2)
    },
    formatIssuedDate(val) {
      if (val == null || val === '') return '—'
      const m = this.$moment(val)
      return m.isValid() ? m.format('YYYY-MM-DD') : '—'
    },
    formatBookFormatLabel,
    getRecordTagItems(record) {
      if (!record) return []
      const tags = record.tags
      if (Array.isArray(tags) && tags.length) {
        return tags
          .map((t) => ({ id: t && t.id, name: (t && t.name) || '' }))
          .filter((t) => t.id != null && t.name)
      }
      const tagIds = record.tagIds
      if (Array.isArray(tagIds) && tagIds.length) {
        const byId = new Map((this.filterTagOptions || []).map((o) => [o.value, o.label]))
        return tagIds
          .map((id) => ({ id, name: byId.get(id) || '' }))
          .filter((t) => t.name)
      }
      return []
    },
    formatTagNamesPlain(record) {
      const items = this.getRecordTagItems(record)
      return items.length ? items.map((t) => t.name).join('、') : ''
    },
    /** findTextBookById 可能不再返回 categoryIds，从 categories 补全 */
    deriveCategoryIdsFromDetail(detail) {
      if (!detail) return []
      if (Array.isArray(detail.categoryIds) && detail.categoryIds.length) {
        return [...detail.categoryIds]
      }
      const cats = detail.categories
      if (!Array.isArray(cats) || !cats.length) return []
      return cats.map((c) => c && c.id).filter((id) => id != null)
    },
    /** findTextBookById 可能不再返回 tagIds，从 tags 补全 */
    deriveTagIdsFromDetail(detail) {
      if (!detail) return []
      if (Array.isArray(detail.tagIds) && detail.tagIds.length) {
        return [...detail.tagIds]
      }
      const tags = detail.tags
      if (!Array.isArray(tags) || !tags.length) return []
      return tags.map((t) => t && t.id).filter((id) => id != null)
    },
    async initFilters() {
      try {
        const [pub, catResp, tagResp] = await Promise.all([
          dictApi.getPublisherCompany(),
          textBookApi.textBookCategoryList({ bookType: EBOOK_BOOK_TYPE }),
          textBookApi.findTextbookTagList({}),
        ])
        this.publisherCompanyList = Array.isArray(pub) ? pub : []
        this.filterCategoryOptions = buildCategoryDisplayOptions(Array.isArray(catResp) ? catResp : [], {
          showBookTypePrefix: false,
        })
        this.filterTagOptions = (Array.isArray(tagResp) ? tagResp : []).map((t) => ({
          value: t.id,
          label: t.name,
        }))
      } catch (e) {
        console.error(e)
      }
    },
    async fetchList(page) {
      this.loading = true
      try {
        const opts = {
          bookType: EBOOK_BOOK_TYPE,
          keyword: this.listQuery.keyword || undefined,
          categoryId: this.listQuery.categoryId,
          tagId: this.listQuery.tagId,
        }
        const resp = await textBookApi.findPagingTextBook(page, opts)
        this.ebookList = resp.list || []
        this.currentPage = resp.pageNum != null ? resp.pageNum : page
        this.total = resp.total != null ? resp.total : 0
      } catch (e) {
        console.error(e)
        this.$message.error(e.message || '加载列表失败')
        this.ebookList = []
      } finally {
        this.loading = false
      }
    },
    handleTableChange(pagination) {
      this.fetchList(pagination.current)
    },

    async openInfoDrawer(id) {
      this.infoDrawerTitle = '电子书信息'
      this.infoDrawerVisible = true
      this.infoLoading = true
      try {
        const detail = await textBookApi.findTextBookById(id)
        const priceYuan = detail.price != null ? detail.price / 100 : undefined
        const issuedDate = detail.issuedDate ? this.$moment(detail.issuedDate) : null
        this.form = {
          ...emptyInfoForm(),
          ...detail,
          price: priceYuan,
          issuedDate,
          thumbUri: undefined,
          cardBgUri: undefined,
          bookFormat: detail.bookFormat != null ? Number(detail.bookFormat) : 0,
        }
      } catch (e) {
        console.error(e)
        this.$message.error(e.message || '加载详情失败')
        this.closeInfoDrawer()
      } finally {
        this.infoLoading = false
      }
    },
    closeInfoDrawer() {
      this.infoDrawerVisible = false
      this.form = emptyInfoForm()
    },
    onThumbUri(uri) {
      this.form.thumbUri = uri
      if (!uri) this.form.thumbUrl = ''
    },
    onCardBgUri(uri) {
      this.form.cardBgUri = uri
      if (!uri) this.form.cardBgUrl = ''
    },
    buildTextBookBasicInfoEditBody() {
      const f = this.form
      const body = {}
      const copyStringFields = [
        'title',
        'summary',
        'note',
        'content',
        'isbn',
        'authors',
        'publishers',
        'chiefEditor',
        'deputyEditor',
        'contributingAuthors',
        'producer',
        'contactInfo',
        'edition',
        'copyrightStatement',
      ]
      for (const k of copyStringFields) {
        if (f[k] !== undefined && f[k] !== null) {
          body[k] = f[k]
        }
      }
      if (f.thumbUri) {
        body.thumbUri = f.thumbUri
      }
      if (f.cardBgUri) {
        body.cardBgUri = f.cardBgUri
      }
      if (f.publisherCompanyId !== undefined && f.publisherCompanyId !== null) {
        body.publisherCompanyId = f.publisherCompanyId
      }
      if (f.price != null && f.price !== '') {
        body.price = Math.round(Number(f.price) * 100)
      }
      if (f.issuedDate) {
        const m = this.$moment(f.issuedDate)
        if (m.isValid()) {
          body.issuedDate = m.startOf('day').toDate()
        }
      }
      if (f.bookFormat !== undefined && f.bookFormat !== null && f.bookFormat !== '') {
        body.bookFormat = Number(f.bookFormat)
      } else {
        body.bookFormat = 0
      }
      return body
    },
    async saveInfo() {
      if (!this.form.id) return
      this.infoSaving = true
      try {
        const body = this.buildTextBookBasicInfoEditBody()
        await textBookApi.updateTextBook(this.form.id, body)
        this.$message.success('保存成功')
        this.closeInfoDrawer()
        await this.fetchList(this.currentPage)
      } catch (e) {
        console.error(e)
        this.$message.error(e.message || '保存失败')
      } finally {
        this.infoSaving = false
      }
    },

    async openCategoryTagModal(row) {
      this.currentEbookId = row.id
      this.categoryTagModalVisible = true
      this.loading = true
      try {
        const [detail, catResp, tagResp] = await Promise.all([
          textBookApi.findTextBookById(row.id),
          textBookApi.textBookCategoryList({ bookType: EBOOK_BOOK_TYPE }),
          textBookApi.findTextbookTagList({}),
        ])
        this.categoryModalOptions = buildCategoryDisplayOptions(Array.isArray(catResp) ? catResp : [], {
          showBookTypePrefix: false,
        })
        const validCat = new Set(this.categoryModalOptions.map((o) => o.value))
        this.tagModalOptions = (Array.isArray(tagResp) ? tagResp : []).map((t) => ({
          value: t.id,
          label: t.name,
        }))
        const validTag = new Set(this.tagModalOptions.map((o) => o.value))

        const rawCatIds = this.deriveCategoryIdsFromDetail(detail)
        const rawTagIds = this.deriveTagIdsFromDetail(detail)
        this.categoryTagForm = {
          categoryIds: rawCatIds.filter((id) => validCat.has(id)),
          tagIds: rawTagIds.filter((id) => validTag.has(id)),
          status: detail.status != null ? detail.status : undefined,
          sortKey:
            detail.sortKey != null && detail.sortKey !== ''
              ? Number(detail.sortKey)
              : null,
          forSale: detail.forSale !== false,
        }
      } catch (e) {
        console.error(e)
        this.$message.error(e.message || '加载失败')
        this.closeCategoryTagModal()
      } finally {
        this.loading = false
      }
    },
    onCategoryIdsChange(values) {
      const valid = new Set(this.categoryModalOptions.map((o) => o.value))
      this.categoryTagForm.categoryIds = (values || []).filter((id) => valid.has(id))
    },
    closeCategoryTagModal() {
      this.categoryTagModalVisible = false
      this.currentEbookId = null
      this.categoryTagForm = {
        categoryIds: [],
        tagIds: [],
        status: undefined,
        sortKey: null,
        forSale: true,
      }
    },
    async saveCategoryAndTag() {
      if (!this.currentEbookId || this.categoryTagSubmitting) return
      this.categoryTagSubmitting = true
      try {
        const sk = this.categoryTagForm.sortKey
        const body = {
          categoryIds: this.categoryTagForm.categoryIds || [],
          tagIds: this.categoryTagForm.tagIds || [],
          status: this.categoryTagForm.status,
          forSale: !!this.categoryTagForm.forSale,
        }
        if (sk != null && sk !== '') {
          body.sortKey = Number(sk)
        }
        await textBookApi.updateTextBookCategoryAndTag(this.currentEbookId, body)
        this.$message.success('保存成功')
        this.closeCategoryTagModal()
        await this.fetchList(this.currentPage)
      } catch (e) {
        console.error(e)
        this.$message.error(e.message || '保存失败')
      } finally {
        this.categoryTagSubmitting = false
      }
    },

    openCreateModal() {
      this.createForm = { title: '' }
      this.createModalVisible = true
    },
    closeCreateModal() {
      this.createModalVisible = false
    },
    async submitCreate() {
      const title = (this.createForm.title || '').trim()
      if (!title) {
        this.$message.warning('请输入书名')
        return
      }
      this.createSubmitting = true
      try {
        await textBookApi.addTextBook({ title, bookType: EBOOK_BOOK_TYPE })
        this.$message.success('创建成功')
        this.closeCreateModal()
        await this.fetchList(1)
      } catch (e) {
        console.error(e)
        this.$message.error(e.message || '创建失败')
      } finally {
        this.createSubmitting = false
      }
    },

    async handleDelete(id) {
      try {
        await textBookApi.deleteTextBook(id)
        this.$message.success('已删除')
        await this.fetchList(this.currentPage)
      } catch (e) {
        console.error(e)
        this.$message.error(e.message || '删除失败')
      }
    },

    openPdfModal(row) {
      if (!row || row.id == null) return
      this.pdfModalEbookId = row.id
      this.pdfModalTitle = (row.title && String(row.title)) || ''
      this.pdfModalPdfFileSize = row.pdfFileSize
      this.ebookPdfPreviewUrl = ''
      this.pdfModalVisible = true
      if (this.pdfModalHasPdf) {
        this.loadEbookPdfPreview(row.id)
      }
    },
    closePdfModal() {
      this.pdfModalVisible = false
      this.pdfModalEbookId = null
      this.pdfModalTitle = ''
      this.pdfModalPdfFileSize = undefined
      this.ebookPdfPreviewUrl = ''
      this.pdfPreviewLoading = false
      this.pdfUploading = false
    },
    async loadEbookPdfPreview(id) {
      this.ebookPdfPreviewUrl = ''
      this.pdfPreviewLoading = true
      try {
        const vo = await textBookApi.getEbookPreviewUrl(id)
        const url = vo && vo.previewUrl ? String(vo.previewUrl) : ''
        this.ebookPdfPreviewUrl = url
        if (!url) {
          this.$message.warning('未获取到预览地址')
        }
      } catch (e) {
        console.error(e)
        this.$message.error(e.message || '加载 PDF 预览失败')
        this.ebookPdfPreviewUrl = ''
      } finally {
        this.pdfPreviewLoading = false
      }
    },
    openPdfPreviewInNewTab() {
      if (!this.ebookPdfPreviewUrl) return
      window.open(this.ebookPdfPreviewUrl, '_blank', 'noopener,noreferrer')
    },
    beforePdfUpload(file) {
      const name = (file.name || '').toLowerCase()
      const ok =
        name.endsWith('.pdf') ||
        file.type === 'application/pdf' ||
        file.type === 'application/x-pdf'
      if (!ok) {
        this.$message.error('仅支持上传 PDF 文件')
        return false
      }
      return true
    },
    handlePdfUploadChange(info) {
      if (info.file.status === 'uploading') {
        this.pdfUploading = true
      }
    },
    async uploadEbookPdf(info) {
      if (!this.pdfModalEbookId) {
        return
      }
      const raw = info.file
      const file = raw.originFileObj != null ? raw.originFileObj : raw
      const uid = raw.uid != null ? raw.uid : file.uid
      this.pdfUploading = true
      try {
        const res = await textBookApi.getPdfStsAk({ textbookId: this.pdfModalEbookId })
        const region = res.endpoint
        const fKey = region.indexOf('//') + 2
        const lKey = region.indexOf('.')
        const client = new OSS({
          region: region.substring(fKey, lKey),
          accessKeyId: res.accessKeyId,
          accessKeySecret: res.accessKeySecret,
          stsToken: res.securityToken,
          bucket: res.bucketName,
        })
        const objectKey = `${res.url}${uid}.pdf`
        const result = await client.multipartUpload(objectKey, file)
        const pdfFileUri = result && result.name ? result.name : objectKey
        await textBookApi.pdfUploadComplete(this.pdfModalEbookId, {
          pdfFileUri,
          pdfFileSize: file.size,
        })
        if (info.onSuccess) {
          info.onSuccess(result, raw)
        }
        this.$message.success('PDF 上传成功')
        await this.fetchList(this.currentPage)
        const row = (this.ebookList || []).find((r) => r.id === this.pdfModalEbookId)
        if (row && row.pdfFileSize != null) {
          this.pdfModalPdfFileSize = row.pdfFileSize
        } else {
          this.pdfModalPdfFileSize = file.size
        }
        await this.loadEbookPdfPreview(this.pdfModalEbookId)
      } catch (e) {
        console.error(e)
        this.$message.error(e.message || 'PDF 上传失败')
        if (info.onError) {
          info.onError(e)
        }
      } finally {
        this.pdfUploading = false
      }
    },
  },
}
</script>

<style scoped lang="less">
.ebook-list {
  padding: 0 0 24px;
}

.toolbar-form {
  margin-bottom: 16px;
}

.thumb-cell {
  height: 30px;
  width: auto;
  max-width: 96px;
  object-fit: contain;
  vertical-align: middle;
}

.danger-link {
  color: #ff4d4f;
}

.categories-cell {
  display: inline-block;
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
}

.ellipsis-plain {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
}

.ebook-meta-col {
  min-width: 0;
  line-height: 1.5;
}

.ebook-meta-line {
  display: flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;

  & + & {
    margin-top: 4px;
  }
}

.ebook-meta-k {
  flex: none;
  color: rgba(0, 0, 0, 0.45);
  font-size: 12px;
}

.ebook-meta-v {
  flex: 1;
  min-width: 0;
}

.tags-cell {
  max-width: 260px;
  line-height: 1.85;
}

.ebook-tag-pill {
  margin: 0 6px 4px 0;
}

.thumb-clickable {
  cursor: zoom-in;
}

.thumb-preview-wrap {
  text-align: center;
}

.thumb-preview-img {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
  vertical-align: middle;
}

.pdf-modal-toolbar {
  margin-bottom: 12px;
}

.pdf-preview-frame-wrap {
  min-height: 50vh;
}

.pdf-preview-iframe {
  width: 100%;
  height: 70vh;
  border: 0;
  vertical-align: top;
}

.ebook-content-editor-wrap {
  min-height: 200px;
}
</style>
